import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import Splitter, { Item as SplitterItem } from 'devextreme-react/splitter';
import { syncThemeToFrame } from '../theme/themeManager';
import { buildWorkspaceTabSrc, workspaceHref } from './editorRoutes';
import { EditorWorkspaceTabs } from './EditorWorkspaceTabs';
import { parseEditorTabId, type EditorTab, type EditorWorkspacePane } from './editorWorkspaceIds';
import { tabsInPane, useEditorWorkspaceStore } from './useEditorWorkspaceStore';
import './editorWorkspaceTabs.css';

export interface EditorWorkspacePageProps {
  filesLinkTo?: string;
  filesLinkLabel?: string;
}

function WorkspacePane({
  pane,
  filesLinkTo,
  tab
}: {
  pane: EditorWorkspacePane;
  filesLinkTo: string;
  tab: EditorTab | null;
}) {
  const focusPane = useEditorWorkspaceStore((state) => state.focusPane);
  const iframeSrc = tab
    ? buildWorkspaceTabSrc(tab.kind, tab.mountId, tab.path, tab.mediaKind, tab.accessMode)
    : '';

  return (
    <div
      className={`editor-workspace-pane${tab ? '' : ' editor-workspace-pane--empty'}`}
      onMouseDown={() => focusPane(pane)}
    >
      <EditorWorkspaceTabs filesLinkTo={filesLinkTo} pane={pane} />
      <div className="editor-workspace-pane__stage">
        {tab && iframeSrc ? (
          <iframe
            key={tab.id}
            className="editor-workspace-page__iframe"
            title={tab.title}
            src={iframeSrc}
            onLoad={(event) => syncThemeToFrame(event.currentTarget.contentWindow)}
          />
        ) : (
          <div className="editor-workspace-page__empty">
            {pane === 'secondary'
              ? 'Open a file or move a tab here.'
              : 'Select a tab to continue editing.'}
          </div>
        )}
      </div>
    </div>
  );
}

export function EditorWorkspacePage({
  filesLinkTo = '/files',
  filesLinkLabel = 'File Browser'
}: EditorWorkspacePageProps) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tabParam = (params.get('tab') ?? '').trim();

  const tabs = useEditorWorkspaceStore((state) => state.tabs);
  const activeTabId = useEditorWorkspaceStore((state) => state.activeTabId);
  const split = useEditorWorkspaceStore((state) => state.split);
  const focusTab = useEditorWorkspaceStore((state) => state.focusTab);

  useEffect(() => {
    if (!tabParam) return;
    const parsed = parseEditorTabId(tabParam);
    if (!parsed) return;
    focusTab(tabParam);
  }, [focusTab, tabParam]);

  const activeTab = useMemo(() => {
    const id = activeTabId ?? tabParam;
    return tabs.find((tab) => tab.id === id) ?? null;
  }, [activeTabId, tabParam, tabs]);

  const primaryTab = useMemo(() => {
    const paneTabs = tabsInPane(tabs, 'primary');
    return paneTabs.find((tab) => tab.id === activeTab?.id) ?? paneTabs[0] ?? null;
  }, [activeTab?.id, tabs]);

  const secondaryTab = useMemo(() => {
    const paneTabs = tabsInPane(tabs, 'secondary');
    return paneTabs.find((tab) => tab.id === activeTab?.id) ?? paneTabs[0] ?? null;
  }, [activeTab?.id, tabs]);

  useEffect(() => {
    if (tabs.length === 0) return;
    if (!activeTab && tabParam) {
      const parsed = parseEditorTabId(tabParam);
      if (parsed) return;
    }
    if (!activeTab && tabs[0]) {
      navigate(workspaceHref(tabs[0].id), { replace: true });
    }
  }, [activeTab, navigate, tabParam, tabs]);

  if (tabs.length === 0) {
    return (
      <div className="ops-fill-page ops-fill-page--bare editor-workspace-page">
        <div className="editor-workspace-page__empty">
          <p>
            No editor tabs are open. Open a file from the{' '}
            <Link to={filesLinkTo}>{filesLinkLabel}</Link>
            . When tabs are open, return here anytime from Files → Workspace.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ops-fill-page ops-fill-page--bare editor-workspace-page">
      <div className="editor-workspace-page__body">
        {split ? (
          <Splitter className="editor-workspace-page__splitter" orientation="horizontal">
            <SplitterItem size="50%" minSize={220}>
              <WorkspacePane pane="primary" filesLinkTo={filesLinkTo} tab={primaryTab} />
            </SplitterItem>
            <SplitterItem size="50%" minSize={220}>
              <WorkspacePane pane="secondary" filesLinkTo={filesLinkTo} tab={secondaryTab} />
            </SplitterItem>
          </Splitter>
        ) : (
          <WorkspacePane pane="primary" filesLinkTo={filesLinkTo} tab={primaryTab} />
        )}
      </div>
    </div>
  );
}
